import { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Field, Form } from 'react-final-form';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { Col, Row } from 'antd';
import classNames from 'classnames';

import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { validatorEmail, validatorRequired } from '~src/services/validators';
import { FieldType } from '~src/types';
import { baseApi } from '~src/services/ApiController';
import TextArea from '~src/shared/form/inputs/TextArea';
import { PageContentContainer } from '~src/styles/containers.sc';
import logger from '~src/services/logger/LlsLogger';
import IconInput, { InputIconType } from '~src/shared/form/inputs/IconInput';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import { StoreI } from '~src/rootReducer';

import { ButtonContainer, SuccessContainer } from './contactUs.sc';
import ContactText from './components/ContactText';
import ContactHeader from './components/ContactUsHeader';

interface StateProps {
  isLoggedIn: boolean;
}

interface FormProps {
  email: string;
  phoneNumber: string;
  message: string;
}

type Props = StateProps & RouteComponentProps<any>;

const ContactForm: FC<Props> = (props) => {
  const [t] = useTranslation();
  const fromString = (queryString.parse(props.location.search).from as string | undefined) || 'unknown';
  const [sent, setSent] = useState(false);

  const submit = (values: FormProps) => {
    const mappedVals: FormProps & { ['from']?: string } = { ...values };
    mappedVals.from = fromString;
    return baseApi
      .post(props.isLoggedIn ? '/message-u' : '/message', mappedVals)
      .then(() => {
        setSent(true);
      })
      .catch((err) => {
        logger.info('Error send contact', {
          error: err,
          info: {
            componentStack: 'ContactUs.tsx',
          },
        });

        return Object.keys(err.response.data).reduce((acc: { [keys: string]: string }, key) => {
          acc[key] = key === 'email' ? 'invalid_email' : 'required';
          return acc;
        }, {});
      });
  };

  const renderField = useCallback(
    ({
      autoFocus,
      input,
      label,
      meta: { touched, error },
      labelKey,
    }: FieldType & { type: InputIconType }) => (
      <IconInput
        {...input}
        autoFocus={autoFocus}
        label={label}
        type={(input.type || 'message') as InputIconType}
        error={touched ? error : undefined}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const renderTextAreaField = useCallback(
    ({ input, label, meta: { touched, error }, labelKey }: FieldType & { type: InputIconType }) => (
      <TextArea
        t={t}
        {...input}
        label={label}
        type={(input.type || 'message') as InputIconType}
        error={touched ? error : null}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const emailPlaceholder = t('x.contact.field.email.placeholder');
  const phonePlaceholder = t('x.contact.field.phone.placeholder');
  const messagePlaceholder = t('x.contact.field.message.placeholder');
  return (
    <div>
      <ContactHeader t={t} isLoggedIn={props.isLoggedIn} />
      {sent ? (
        <SuccessContainer data-translation="x.contact.send.success_message">
          {t('x.contact.send.success_message')}
        </SuccessContainer>
      ) : (
        <PageContentContainer $paddingBottom="60px">
          <Row gutter={30} type="flex" align="middle">
            <Col lg={12} md={14} sm={24}>
              <Form onSubmit={submit}>
                {({ handleSubmit, submitting }) => (
                  <form
                    className={classNames({ padded: !props.isLoggedIn })}
                    style={{ marginTop: '30px' }}
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <Field
                      autoFocus
                      name="email"
                      label={emailPlaceholder}
                      type="email"
                      children={renderField}
                      validate={composeValidators([validatorRequired, validatorEmail])}
                      labelKey="x.contact.field.email.placeholder"
                    />
                    <Field
                      name="phoneNumber"
                      label={phonePlaceholder}
                      type="phone"
                      children={renderField}
                      labelKey="x.contact.field.phone.placeholder"
                    />
                    <Field
                      name="message"
                      label={messagePlaceholder}
                      type="message"
                      children={renderTextAreaField}
                      validate={validatorRequired}
                      labelKey="x.contact.field.message.placeholder"
                    />
                    <ButtonContainer>
                      <PrimaryButtonLoading isLoading={submitting}>
                        <span data-translation="x.contact.button.send_message">
                          {t('x.contact.button.send_message')}
                        </span>
                      </PrimaryButtonLoading>
                    </ButtonContainer>
                  </form>
                )}
              </Form>
            </Col>
            <Col
              lg={{ span: 8, offset: 4 }}
              md={{ span: 8, offset: 2 }}
              sm={24}
              style={{ marginTop: '15px' }}
            >
              <ContactText t={t} />
            </Col>
          </Row>
        </PageContentContainer>
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  isLoggedIn: state.user.session.authStatus,
});

const ContactFormC = connect(mapStateToProps)(ContactForm);

export default ContactFormC;
